// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import { allCoachAssignmentsStored } from "../utilities/coachAssignmentResults";
import {
    AdvancedProfileSearchArgs,
    CampusLocation,
    CclManagedProfileProperties,
    CountryLocation,
    DeclineContractRequest,
    GenerateContractPdfResponse,
    GenerateContractRequest,
    PkeyResponse,
    Profile,
    ProfileAttributeSearch,
    ProfileContract,
    ProfileFeeStructure,
    ProfileReportingLocation,
    ProfileReportingRegion,
    ProfileRole,
    SignContractRequest,
    StateLocation,
    UpdateCclManagedProfilePropertiesPayload,
    UpdateContractEligibilityRequest,
} from "./types/enterpriseCoachingApiTypes";
import {
    AssignedCoachesResponse,
    AvailableCoachesResponse,
    CoachAssignmentEndpointPayload,
    CoachAssignmentsResponse,
    SessionDetailResourceAppointment,
} from "./types/sessionDetailTypes";
import { KeyClient, ProfileKeyClient } from "./types/resources/clientTypes";
import { Biography } from "./types/bioApiTypes";
import {
    AttributeData,
    UpdateProfile,
    SaveProfileDemographicsData,
    AllAttributeOptions,
} from "./types/myAccessTypes";
import { ResourceDocument } from "./types/search.service.types";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedEnterpriseCoachingApi = createApi({
    reducerPath: "cclTokenedEnterpriseCoachingApi",
    tagTypes: [
        "Coach",
        "CoachAssignments",
        "Profile",
        "KeyClients",
        "ProfileData",
        "AttributeOptions",
        "AllProfileContracts",
        "ProfileContract",
        "ContractData",
        "ParticipantsCoachAssignments",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        storeCclParticipantCoachAssignments: builder.mutation<
            CoachAssignmentsResponse,
            CoachAssignmentEndpointPayload
        >({
            query: (args) => ({
                url: `enterprise-coach-assignment/v1.0/eventcoaches/${args.eskey}/assign`,
                method: "POST",
                body: args.payload,
            }),
            // only refetch when every change was stored. A failed or partial save must not refetch,
            // as the panels replace their unsaved working list with the refetched data
            invalidatesTags: (result, _, args: CoachAssignmentEndpointPayload) =>
                allCoachAssignmentsStored(
                    result?.coachAssignmentResponses,
                    args.payload.coachAssignments.length
                )
                    ? [{ type: "CoachAssignments", id: args.eskey }]
                    : [],
        }),
        updateCclManagedProfileProperties: builder.mutation<
            CclManagedProfileProperties,
            UpdateCclManagedProfilePropertiesPayload
        >({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/ccl-managed-props/${args.imKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: UpdateCclManagedProfilePropertiesPayload) => [
                { type: "Profile", id: args.payload.pKey },
            ],
        }),
        getAllContractsByProfileKey: builder.query<ProfileContract[], string>({
            query: (profileKey) =>
                `enterprise-coaching/v1/CoachingContract/all-contracts/${profileKey}`,
            providesTags: (_, __, profileKey) => [{ type: "AllProfileContracts", id: profileKey }],
        }),
        getOpenContractByProfileKey: builder.query<ProfileContract, string>({
            query: (profileKey) =>
                `enterprise-coaching/v1/CoachingContract/opencontract/${profileKey}`,
            providesTags: (_, __, profileKey) => [{ type: "ProfileContract", id: profileKey }],
        }),
        signContract: builder.mutation<ProfileContract, SignContractRequest>({
            query: (request) => ({
                url: `enterprise-coaching/v1/CoachingContract/signcontract/${request.profileKey}`,
                method: "PUT",
                body: request.payload,
            }),
            invalidatesTags: (_, __, request: SignContractRequest) => [
                { type: "ProfileContract", id: request.profileKey },
                { type: "AllProfileContracts", id: request.profileKey },
            ],
        }),
        declineContract: builder.mutation<boolean, DeclineContractRequest>({
            query: (request) => ({
                url: `enterprise-coaching/v1/CoachingContract/declinecontract/${request.profileKey}`,
                method: "PUT",
                body: request.payload,
            }),
            invalidatesTags: (_, __, request: DeclineContractRequest) => [
                { type: "ProfileContract", id: request.profileKey },
                { type: "AllProfileContracts", id: request.profileKey },
            ],
        }),
        updateContractEligibility: builder.mutation<boolean, UpdateContractEligibilityRequest>({
            query: (request) => ({
                url: `enterprise-coaching/v1/CoachingContract/updateeligibility/${request.profileKey}`,
                method: "PUT",
                body: request.payload,
            }),
            invalidatesTags: (_, __, request: UpdateContractEligibilityRequest) => [
                { type: "ProfileContract", id: request.profileKey },
            ],
        }),
        generateSignedContractPdf: builder.mutation<
            GenerateContractPdfResponse,
            GenerateContractRequest
        >({
            query: (request) => ({
                url: "ccl-access2/Resources/generate_contract",
                method: "POST",
                body: request,
            }),
        }),
        getKeyClientsByProfileKey: builder.query<ProfileKeyClient[], string>({
            query: (profileKey) =>
                `enterprise-coaching/v1/CoachingProfile/key-clients/${profileKey}`,
            providesTags: (_, __, profileKey) => [{ type: "KeyClients", id: profileKey }],
        }),
        getKeyClients: builder.query<KeyClient[], void>({
            query: () => `enterprise-coaching/v1/CoachingProfile/key-clients/all`,
        }),
        addKeyClient: builder.mutation<boolean, ProfileKeyClient>({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/key-clients/${args.pKey}/${args.kcKey}`,
                method: "POST",
            }),
            invalidatesTags: (_, __, args: ProfileKeyClient) => [
                { type: "KeyClients", id: args.pKey },
            ],
        }),
        deleteKeyClient: builder.mutation<boolean, ProfileKeyClient>({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/key-clients/${args.pKey}/${args.kcKey}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, args: ProfileKeyClient) => [
                { type: "KeyClients", id: args.pKey },
            ],
        }),
        getDemographicsByProfileKey: builder.query<Profile, string>({
            query: (profileKey) =>
                `enterprise-coaching/v1/CoachingProfile/demographics/${profileKey}`,
            providesTags: (_, __, profileKey) => [{ type: "Profile", id: profileKey }],
        }),
        postDemographicsByProfileKey: builder.mutation<string, SaveProfileDemographicsData>({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/demographics/${args.pKey}`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: SaveProfileDemographicsData) => [
                { type: "Profile", id: args.pKey },
            ],
        }),
        getUsedTimeZones: builder.query<string[], void>({
            query: () => "enterprise-coaching/v1/Lookup/used-timezones",
        }),
        getRolesByProfileKey: builder.query<ProfileRole[], string>({
            query: (profileKey) => `enterprise-coaching/v1/CoachingProfile/roles/${profileKey}`,
        }),
        getAssignedCoachesBySessionKey: builder.query<AssignedCoachesResponse, string>({
            query: (eskey) => `enterprise-coach-assignment/v1.0/eventcoaches/${eskey}/assigned`,
            providesTags: (_, __, eskey) => [{ type: "CoachAssignments", id: eskey }],
        }),
        getParticipantsAssignedCoaches: builder.query<AssignedCoachesResponse, string>({
            query: (esikey) =>
                `enterprise-coach-assignment/v1.0/eventcoaches/participant/${esikey}`,
            providesTags: (_, __, esikey) => [{ type: "ParticipantsCoachAssignments", id: esikey }],
        }),
        getAvailableCoachesBySessionKey: builder.query<AvailableCoachesResponse, string>({
            query: (eskey) => `enterprise-coach-assignment/v1.0/eventcoaches/${eskey}/available`,
        }),
        getResourceAppointmentsBySessionKey: builder.query<
            SessionDetailResourceAppointment[],
            string
        >({
            query: (eskey) =>
                `enterprise-coach-assignment/v1.0/resources/appointments/event/${eskey}`,
        }),
        getAllProfileReportingLocations: builder.query<ProfileReportingLocation[], void>({
            query: () => "enterprise-coaching/v1/Lookup/profile-reporting-locations",
        }),
        getAllProfileReportingRegions: builder.query<ProfileReportingRegion[], void>({
            query: () => "enterprise-coaching/v1/Lookup/profile-reporting-regions",
        }),
        getAllCampuses: builder.query<CampusLocation[], void>({
            query: () => "enterprise-coaching/v1/Lookup/campuses",
        }),
        getAllCountries: builder.query<CountryLocation[], void>({
            query: () => "enterprise-coaching/v1/Lookup/countries",
        }),
        getRoles: builder.query<ProfileRole[], void>({
            query: () => "enterprise-coaching/v1/Lookup/roles",
        }),
        getStatesandProvincesByCMKey: builder.query<StateLocation[], number>({
            query: (cmkey) => `enterprise-coaching/v1/Lookup/states-and-provinces/${cmkey}`,
        }),
        getFeeStructures: builder.query<ProfileFeeStructure[], void>({
            query: () => "enterprise-coaching/v1/Lookup/fee-structures",
        }),
        getAllAttributeOptions: builder.query<AllAttributeOptions, void>({
            query: () => "enterprise-coaching/v1/ProfileAttributes",
            providesTags: (_, __, ___) => [{ type: "ProfileData" }],
        }),
        getProfilesAttributes: builder.query<AttributeData[], string>({
            query: (pkey) => `enterprise-coaching/v1/ProfileAttributes/${pkey}`,
            providesTags: (_, __, pkey) => [{ type: "ProfileData" }],
        }),
        getProfilesAttributesByImKey: builder.query<AttributeData[], string>({
            query: (imkey) => `enterprise-coaching/v1/ProfileAttributes/by_imkey/${imkey}`,
            providesTags: (_, __, pkey) => [{ type: "ProfileData" }],
        }),
        addProfileAttributes: builder.mutation<string, UpdateProfile>({
            query: (args) => ({
                url: `enterprise-coaching/v1/ProfileAttributes`,
                method: "PUT",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: UpdateProfile) => [{ type: "ProfileData" }],
        }),
        updateProfileAttributes: builder.mutation<string, UpdateProfile>({
            query: (args) => ({
                url: `enterprise-coaching/v1/ProfileAttributes`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, args: UpdateProfile) => [{ type: "ProfileData" }],
        }),
        deleteProfileAttribute: builder.mutation<string, UpdateProfile>({
            query: (args) => ({
                url: `enterprise-coaching/v1/ProfileAttributes/${args.paKey}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, args: UpdateProfile) => [{ type: "ProfileData" }],
        }),
        saveProfilePicture: builder.mutation<string, any>({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/photo/${args.imKey}`,
                method: "POST",
                body: args.payload.picFile,
            }),
        }),
        getAllBios: builder.query<Biography[], any>({
            query: () => ({
                url: "enterprise-coaching/v1/Biography/biography/all",
                method: "GET",
            }),
        }),
        getAllContracts: builder.query<ProfileContract[], any>({
            query: () => ({
                url: "enterprise-coaching/v1/CoachingContract/all-contracts",
                method: "GET",
            }),
            providesTags: (_, __, ___) => [{ type: "ContractData" }],
        }),
        searchAttributesByPitKey: builder.mutation<ProfileAttributeSearch[], any>({
            query: (args) => ({
                url: `enterprise-coaching/v1/ProfileAttributes/by-pitkey/${args.pitKey}`,
                method: "GET",
            }),
        }),
        advancedProfileSearch: builder.mutation<number[], AdvancedProfileSearchArgs>({
            query: (args) => ({
                url: `enterprise-coaching/v1/CoachingProfile/advanced-search`,
                method: "POST",
                body: args,
            }),
        }),
        getPkeyByImKey: builder.query<PkeyResponse, string>({
            query: (imKey) => `enterprise-coaching/v1.0/CoachingProfile/profilekey/${imKey}`,
        }),
        getActiveResources: builder.query<ResourceDocument[], void>({
            query: () => "enterprise-coaching/v1/Resources/active",
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useStoreCclParticipantCoachAssignmentsMutation,
    useUpdateCclManagedProfilePropertiesMutation,
    useGetAllContractsByProfileKeyQuery,
    useGetOpenContractByProfileKeyQuery,
    useSignContractMutation,
    useDeclineContractMutation,
    useUpdateContractEligibilityMutation,
    useGenerateSignedContractPdfMutation,
    useGetKeyClientsByProfileKeyQuery,
    useGetDemographicsByProfileKeyQuery,
    useGetRolesByProfileKeyQuery,
    useGetKeyClientsQuery,
    useAddKeyClientMutation,
    useDeleteKeyClientMutation,
    useGetAssignedCoachesBySessionKeyQuery,
    useGetAvailableCoachesBySessionKeyQuery,
    useGetParticipantsAssignedCoachesQuery,
    useGetResourceAppointmentsBySessionKeyQuery,
    useGetAllProfileReportingLocationsQuery,
    useGetAllProfileReportingRegionsQuery,
    useGetAllAttributeOptionsQuery,
    useGetProfilesAttributesQuery,
    useAddProfileAttributesMutation,
    useUpdateProfileAttributesMutation,
    useDeleteProfileAttributeMutation,
    usePostDemographicsByProfileKeyMutation,
    useGetAllCountriesQuery,
    useGetStatesandProvincesByCMKeyQuery,
    useLazyGetStatesandProvincesByCMKeyQuery,
    useGetFeeStructuresQuery,
    useSaveProfilePictureMutation,
    useGetAllBiosQuery,
    useGetAllContractsQuery,
    useGetRolesQuery,
    useAdvancedProfileSearchMutation,
    useGetUsedTimeZonesQuery,
    useGetPkeyByImKeyQuery,
    useLazyGetPkeyByImKeyQuery,
    useGetAllCampusesQuery,
    useGetActiveResourcesQuery,
} = cclTokenedEnterpriseCoachingApi;
